import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery, gql } from '@apollo/client';
import { useAuth } from './AuthContext';

// GraphQL query for trust score
const GET_USER_TRUST = gql`
  query GetUserAnalytics {
    userAnalytics(timeRange: "month") {
      trustScore
      lifetimeJobsCompleted
      completionRate
      groupsJoined
    }
  }
`;

// Create the trust context
const TrustContext = createContext({
  userTrust: null,
  calculateTrustScore: () => {},
  updateTrustScore: () => {},
  getTrustLevel: () => {},
  getTrustColor: () => {},
  getTrustBadge: () => {},
  loading: false,
  error: null,
});

// Custom hook to use the trust context
export const useTrust = () => useContext(TrustContext);

// Provider component
export const TrustProvider = ({ children }) => {
  const { user } = useAuth();
  
  const [userTrust, setUserTrust] = useState({
    score: 3.0,  // Default for new users
    breakdown: {
      groupParticipation: 3.0,
      jobCompletion: 3.0,
      peerRatings: 3.0,
      skillVerification: 3.0,
      responseTime: 3.0
    },
    completedJobs: 0,
    totalRatings: 0,
    badges: [],
    level: "Building",
    history: [],
    lastUpdated: new Date()
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch real trust score from backend
  const { data: trustData, loading: queryLoading, error: queryError } = useQuery(GET_USER_TRUST, {
    skip: !user,
    fetchPolicy: 'cache-and-network',
    pollInterval: 60000 // Refresh every minute
  });

  // Update userTrust when data is fetched
  useEffect(() => {
    if (trustData?.userAnalytics) {
      const analytics = trustData.userAnalytics;
      const score = analytics.trustScore || 3.0;
      
      setUserTrust(prev => ({
        ...prev,
        score: score,
        completedJobs: analytics.lifetimeJobsCompleted || 0,
        completionRate: analytics.completionRate || 100,
        groupsJoined: analytics.groupsJoined || 0,
        level: getTrustLevel(score),
        lastUpdated: new Date()
      }));
    }
  }, [trustData]);

  // Update loading state
  useEffect(() => {
    setLoading(queryLoading);
  }, [queryLoading]);

  // Update error state
  useEffect(() => {
    if (queryError) {
      setError(queryError.message);
    }
  }, [queryError]);

  // Trust score calculation with weights
  const calculateTrustScore = (breakdown) => {
    const weights = {
      groupParticipation: 0.20,
      jobCompletion: 0.30,
      peerRatings: 0.25,
      skillVerification: 0.15,
      responseTime: 0.10
    };
    
    const score = Object.entries(breakdown).reduce((total, [key, value]) => {
      return total + (value * weights[key]);
    }, 0);
    
    return parseFloat(score.toFixed(1));
  };

  // Get trust level based on score
  const getTrustLevel = (score) => {
    if (score >= 4.5) return "Expert";
    if (score >= 4.0) return "Trusted";
    if (score >= 3.5) return "Reliable";
    if (score >= 3.0) return "Building";
    return "New Member";
  };

  // Get color for trust score display
  const getTrustColor = (score) => {
    if (score >= 4.5) return "text-green-600";
    if (score >= 4.0) return "text-blue-600";
    if (score >= 3.5) return "text-yellow-600";
    if (score >= 3.0) return "text-orange-600";
    return "text-gray-600";
  };

  // Get background color for badges
  const getTrustBgColor = (score) => {
    if (score >= 4.5) return "bg-green-100";
    if (score >= 4.0) return "bg-blue-100";
    if (score >= 3.5) return "bg-yellow-100";
    if (score >= 3.0) return "bg-orange-100";
    return "bg-gray-100";
  };

  // Get trust badge configuration
  const getTrustBadge = (score) => {
    const level = getTrustLevel(score);
    const color = getTrustColor(score);
    const bgColor = getTrustBgColor(score);
    
    return {
      level,
      color,
      bgColor,
      icon: score >= 4.0 ? '⭐' : '📈',
      description: getDescription(level)
    };
  };

  // Get description for trust level
  const getDescription = (level) => {
    const descriptions = {
      "Expert": "Top community contributor with exceptional reliability",
      "Trusted": "Highly reliable member eligible for premium jobs",
      "Reliable": "Consistent participant with good track record",
      "Building": "Active member building community trust",
      "New Member": "Welcome! Complete activities to build trust"
    };
    return descriptions[level] || "";
  };

  // Update trust score based on activity
  const updateTrustScore = async (userId, factor, value) => {
    setLoading(true);
    setError(null);
    
    try {
      // In production, this would be an API call
      // For now, we'll simulate the update
      const newBreakdown = {
        ...userTrust.breakdown,
        [factor]: Math.min(5.0, Math.max(0, value))
      };
      
      const newScore = calculateTrustScore(newBreakdown);
      const newLevel = getTrustLevel(newScore);
      
      const updatedTrust = {
        ...userTrust,
        score: newScore,
        breakdown: newBreakdown,
        level: newLevel,
        lastUpdated: new Date(),
        history: [
          {
            date: new Date(),
            factor,
            change: newScore - userTrust.score,
            newScore
          },
          ...userTrust.history
        ].slice(0, 10) // Keep last 10 history items
      };
      
      setUserTrust(updatedTrust);
      
      // Return the updated trust data
      return updatedTrust;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Check eligibility for specific roles/jobs
  const checkEligibility = (requiredScore) => {
    return userTrust.score >= requiredScore;
  };

  // Get trust requirements message
  const getTrustRequirement = (requiredScore) => {
    if (userTrust.score >= requiredScore) {
      return { eligible: true, message: "You meet the trust requirements!" };
    }
    
    const gap = requiredScore - userTrust.score;
    const suggestions = [];
    
    // Analyze which factors need improvement
    Object.entries(userTrust.breakdown).forEach(([factor, score]) => {
      if (score < 4.0) {
        suggestions.push(getImprovementSuggestion(factor));
      }
    });
    
    return {
      eligible: false,
      message: `You need ${gap.toFixed(1)} more trust points for this role.`,
      suggestions: suggestions.slice(0, 3) // Top 3 suggestions
    };
  };

  // Get improvement suggestions
  const getImprovementSuggestion = (factor) => {
    const suggestions = {
      groupParticipation: "Join and complete more group orders",
      jobCompletion: "Complete logistics jobs to build reputation",
      peerRatings: "Maintain positive interactions with members",
      skillVerification: "Verify your skills and certifications",
      responseTime: "Respond quickly to messages and updates"
    };
    return suggestions[factor] || "Continue participating in the community";
  };

  // Context value
  const value = {
    userTrust,
    setUserTrust,
    calculateTrustScore,
    updateTrustScore,
    getTrustLevel,
    getTrustColor,
    getTrustBadge,
    checkEligibility,
    getTrustRequirement,
    loading,
    error,
  };

  return (
    <TrustContext.Provider value={value}>
      {children}
    </TrustContext.Provider>
  );
};